import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../../components/Icon';
import Button, { buttonType } from '../../components/Button';
import Sidebar from '../../components/Sidebar';
import LoginForm from '../../components/LoginForm';
import OuterLayer from '../../containers/OuterLayer';
import { useLabels } from '../../hooks';
import { PageContext, DeviceContext, OutersContainerContext, UserContext, UserType } from '../../contexts';
import { consumeReturnTo } from '../../utils/helpers';
import styles from './Home.module.scss';
import { env } from '../../config';
import klalit from "./images/klalit.png";
import yoelGeva from "./images/yoelGeva.png";
import TelAvivJafo from "./images/TelAvivJafo.png";
import YediotAchronot from "./images/YediotAchronot.png";
import eco99fm from "./images/eco99fm.png";
import galatz from "./images/galatz.png";
import harvard from "./images/harvard.png";
import globs from "./images/globs.png";
import uniBarIlan from "./images/uniBarIlan.png";
import McCANN from "./images/McCANN.png";
import uniBenGuryon from "./images/uniBenGuryon.png";
import kan11 from "./images/kan11.png";
import Wix from "./images/Wix.png";
import Ono from "./images/Ono.png";
import kneset from "./images/kneset.png";
import achva from "./images/achva.png";
import uniTelAviv from "./images/uniTelAviv.png";
import reshet13 from "./images/reshet13.png";
import vaytzman from "./images/vaytzman.png";
import mishpatim from "./images/mishpatim.png";
import MachonAsifLogo from "./images/MachonAsifLogo.png";
import uniJerusalem from "./images/uniJerusalem.png";
import Film from '../../components/Film/index'

const pageKey = "home";

const Home = () => {
  const { getLabel, getLocalUrlByLanguage, getDataByDirection } = useLabels();
  const { setPageKey } = useContext(PageContext);
  const { isMobileView } = useContext(DeviceContext);
  const { appLayoutHeaderSectionContainer } = useContext(OutersContainerContext);
  const { hasPermissions, userType } = useContext(UserContext);
  const { pathname: locationPathname } = useLocation();
  const navigate = useNavigate();

  const showLogin = useMemo(() => {
    return locationPathname === getLocalUrlByLanguage('/login');
  }, [locationPathname, getLocalUrlByLanguage]);

  useEffect(() => {
    const migrationInProgress = (() => { try { return sessionStorage.getItem('migrationInProgress') === '1'; } catch (e) { return false; } })();
    if (showLogin && !migrationInProgress && (hasPermissions || userType !== UserType.guest)) {
      const returnTo = hasPermissions ? consumeReturnTo() : null;
      navigate(returnTo || getLocalUrlByLanguage(`/`));
    }
  }, [hasPermissions, getLocalUrlByLanguage, navigate, userType, showLogin]);

  useEffect(() => {
    setPageKey(showLogin ? 'login' : pageKey);
  }, [setPageKey, showLogin]);

  const onSignUpButtonClickHandle = useCallback(() => {
    if (userType === UserType.guest && (env.REACT_APP_ORG_ENABLE() || env.IS_EDU_SIDE)) {
      window.open(`${getLabel('main_site')}purchase`, "_blank");
    }
    else {
      navigate(getLocalUrlByLanguage(`/purchase`));
    }
  }, [getLabel, getLocalUrlByLanguage, navigate, userType]);

  const onLogInButtonClickHandle = useCallback(() => {
    navigate(getLocalUrlByLanguage(`/login`));
  }, [getLocalUrlByLanguage, navigate]);

  const onLiginCloseHandle = useCallback(() => {
    navigate(getLocalUrlByLanguage(`/`));
  }, [getLocalUrlByLanguage, navigate]);

  const topics = useMemo(() => {
    const groups = isMobileView ? [[1, 2, 3, 4], [5, 6, 7, 8]] : [[1, 2, 3, 4, 5, 6, 7, 8]];

    return <div>
      {
        groups.map((group, group_index) => <div className={styles.topics} key={`home_page_topics-${group_index}`}>{
          group.map((index) => {
            const key = `home_page_topic_${index}`;
            return <span key={key}>{getLabel(key)}</span>
          })
        }</div>)
      }
    </div>;
  }, [isMobileView, getLabel]);

  const loginForm = useMemo(() => {
    return <LoginForm />;
  }, []);

  const signUpButton = useMemo(() => {
    return <Button type={buttonType.premium} className={styles.sign_up_button} onClick={onSignUpButtonClickHandle} >{getLabel(userType === UserType.guest ? 'sign_up_button' : 'home_page_subscription_button')}</Button>;
  }, [getLabel, onSignUpButtonClickHandle, userType]);

  const logInButton = useMemo(() => {
    return userType === UserType.guest && <Button type={buttonType.link} className={styles.log_in_button} onClick={onLogInButtonClickHandle} >{getLabel('log_in_button')}</Button>;
  }, [getLabel, onLogInButtonClickHandle, userType]);

  const images1 = [
    { src: yoelGeva, alt: 'yoel Geva' },
    { src: Ono, alt: 'Ono Academic College' },
    { src: galatz, alt: 'galatz' },
    { src: eco99fm, alt: 'eco99fm' },
    { src: YediotAchronot, alt: 'Yediot Achronot' },
    { src: TelAvivJafo, alt: 'Tel Aviv Jafo' },
    { src: klalit, alt: 'klalit' },
  ];
  const images2 = [
    { src: MachonAsifLogo, alt: 'Machon Asif' },
    { src: harvard, alt: 'harvard' },
    { src: uniBarIlan, alt: 'Bar Ilan university' },
    { src: McCANN, alt: 'McCANN' },
    { src: uniBenGuryon, alt: 'ben guryon university' },
    { src: kan11, alt: 'kan 11' },
    { src: Wix, alt: 'wix' },
    { src: globs, alt: 'globs' },
  ];
  const images3 = [
    { src: uniJerusalem, alt: 'evrit university' },
    { src: mishpatim, alt: 'Ministry of Justice' },
    { src: vaytzman, alt: 'vaytzman' },
    { src: reshet13, alt: 'reshet13' },
    { src: uniTelAviv, alt: 'tel aviv university' },
    { src: achva, alt: 'achva' },
    { src: kneset, alt: 'kneset' },
  ];


  const layout = useMemo(() => {
    if (isMobileView && showLogin) {
      return <div className={`${styles.container} ${styles.container_login}`}>{loginForm}</div>;
    }


    if (isMobileView) {
      return <>
        <OuterLayer container={appLayoutHeaderSectionContainer} TagWrapper='div' className={styles.buttons}>
          {signUpButton}
          {logInButton}
        </OuterLayer>
        <div className={styles.container}>
          <div className={styles.scroll_anchor}></div>
          {topics}
          <div className={`${styles.block} ${styles.block_about}`}>
            <div className={styles.about_content}>
              <div className={styles.block_title}>{getLabel('home_page_about_title')}</div>
              <div className={styles.about_text}>
                <div dangerouslySetInnerHTML={{ __html: getLabel('home_page_about_text_1') }} />
                <div className={styles.about_image}></div>
                <br />
                <div dangerouslySetInnerHTML={{ __html: getLabel('home_page_about_text_2') }} />
              </div>
            </div>
          </div>
          <div className={`${styles.block} ${styles.block_customers}`}>
            <div className={styles.block_title}>{getLabel('home_page_customers_title')}</div>
            <div className={styles.customers}>
              <Film images={images1} direction={'rtl'} Duration={25.57} />
              <Film images={images2} direction={'ltr'} Duration={24.25} />
              <Film images={images3} direction={'rtl'} Duration={25.57} />
            </div>
          </div>
        </div>
      </>;
    }

    return <>
      <div className={styles.container}>
        <div className={`${styles.block} ${styles.block_log_in}`}>
          <div>
            <div>
              <Icon className={`${styles.logo} ${styles[`logo_${getLabel('lang')}`]}`} icon={`logo_large_${getLabel('lang')}`} />
              <div className={styles.opening_text}>{getLabel('home_page_opening_text')}</div>
              <div className={styles.buttons}>
                {signUpButton}
                {logInButton}
              </div>
            </div>
          </div>
          {topics}
        </div>
        <div className={`${styles.block} ${styles.block_about}`}>
          <div className={styles.about_content}>
            <div className={styles.block_title}>{getLabel('home_page_about_title')}</div>
            <div className={styles.about_text}>
              <div dangerouslySetInnerHTML={{ __html: getLabel('home_page_about_text_1') }} />
              <br />
              <div dangerouslySetInnerHTML={{ __html: getLabel('home_page_about_text_2') }} />
            </div>
          </div>
          <div className={styles.about_image}></div>
        </div>
        <div className={`${styles.block} ${styles.block_customers}`}>
          <div className={styles.block_title}>{getLabel('home_page_customers_title')}</div>
          <div className={styles.customers}>
            <Film images={images1} direction={'rtl'} Duration={25.57} />
            <Film images={images2} direction={'ltr'} Duration={26.57} />
            <Film images={images3} direction={'rtl'} Duration={28.57} />
          </div>
        </div>
      </div>
      <Sidebar isOpen={showLogin} onClose={onLiginCloseHandle} side={getDataByDirection('left', 'right')}>{loginForm}</Sidebar>
    </>;
  }, [appLayoutHeaderSectionContainer, getDataByDirection, getLabel, isMobileView, loginForm, onLiginCloseHandle, onLogInButtonClickHandle, onSignUpButtonClickHandle, showLogin, topics, userType]);

  return layout;
};

export default Home;
